import type { DeviceInfo } from '@tonconnect/protocol';

import type { ChainIdByChain, EvmTransactionParams } from '../../../api/dappProtocols/adapters/walletConnect/types';
import type { DappConnectionResult, DappSessionChain } from '../../../api/dappProtocols/types';
import type { ApiChain, ApiNetwork } from '../../../api/types';
import type { RegisterEvmInjectedWalletCb } from '../../injectedConnector/evmConnector';
import type { BridgeApi } from '../provider/bridgeApi';
import type { BrowserTonConnectBridgeMethods } from '../provider/tonConnectBridgeApi';

interface TonConnectProperties {
  deviceInfo: DeviceInfo;
  protocolVersion: number;
  isWalletBrowser: boolean;
}

type ApiMethodName = keyof BridgeApi;
type ApiArgs<T extends ApiMethodName = any> = Parameters<Required<BridgeApi>[T]>;
type ApiMethodResponse<T extends ApiMethodName = any> = ReturnType<Required<BridgeApi>[T]>;

interface RequestState {
  resolve: AnyToVoidFunction;
  reject: AnyToVoidFunction;
}

interface OutMessageData {
  channel: string;
  messageId: string;
  type: 'callMethod';
  name: ApiMethodName;
  args: ApiArgs;
}

type InMessageData = {
  channel: string;
  messageId: string;
  type: 'methodResponse';
  response?: ApiMethodResponse;
  error?: { message: string };
} | {
  channel: string;
  messageId: string;
  type: 'update';
  update: string;
};

type CordovaPostMessageTarget = { postMessage: AnyToVoidFunction };
type Handler = (update: string) => void;

/**
 * Allows calling functions, provided by another messenger (the parent window, or the Capacitor main view), in this messenger.
 * The other messenger must provide the functions using `createReverseIFrameInterface`.
 *
 * `PostMessageConnect` is not used here (as any other dependencies) because this needs to be easily stringified.
 */
export function initConnector(
  bridgeKey: string,
  channel: string,
  target: Window | CordovaPostMessageTarget,
  tonConnectProperties: TonConnectProperties,
  appName: string,
  icon: string,
  registerEvmInjectedWallet: RegisterEvmInjectedWalletCb,
) {
  if ((window as any)[bridgeKey]) return;

  const TON_CONNECT_BRIDGE_METHODS = ['connect', 'restoreConnection', 'disconnect', 'send'] as const;

  const requestStates = new Map<string, RequestState>();
  const tonUpdateHandlers = new Set<Handler>();

  setupPostMessageHandler();
  setupGlobalOverrides();
  initTonConnect();
  initEvmConnect();

  function setupPostMessageHandler() {
    window.addEventListener('message', ({ data }) => {
      const message = data as InMessageData;

      if (message.channel !== channel) {
        return;
      }

      if (message.type === 'methodResponse') {
        const requestState = requestStates.get(message.messageId);
        if (!requestState) return;

        requestStates.delete(message.messageId);

        if (message.error) {
          requestState.reject(message.error);
        } else {
          requestState.resolve(message.response);
        }
      } else if (message.type === 'update') {
        tonUpdateHandlers.forEach((handler) => handler(message.update));
      }
    });
  }

  function setupGlobalOverrides() {
    window.open = (url) => {
      url = sanitizeUrl(url);
      if (url) {
        void callApi('window:open', { url });
      }

      // eslint-disable-next-line no-null/no-null
      return null;
    };

    window.close = () => {
      void callApi('window:close');
    };

    window.addEventListener('click', (e) => {
      if (!(e.target instanceof HTMLElement)) return;

      const { href, target } = e.target.closest('a') || {};
      if (href && (target === '_blank' || !href.startsWith('http'))) {
        e.preventDefault();

        const url = sanitizeUrl(href);
        if (url) {
          void callApi('window:open', { url });
        }
      }
    }, false);
  }

  function initTonConnect() {
    const methods = Object.fromEntries(TON_CONNECT_BRIDGE_METHODS.map((name) => {
      return [
        name,
        (...args: Parameters<BrowserTonConnectBridgeMethods[typeof name]>) => callApi(`tonConnect:${name}`, ...args),
      ];
    }));

    function addUpdateHandler(cb: Handler) {
      tonUpdateHandlers.add(cb);

      return () => {
        tonUpdateHandlers.delete(cb);
      };
    }

    (window as any)[bridgeKey] = {
      ionconnect: {
        ...tonConnectProperties,
        ...methods,
        listen: addUpdateHandler,
      },
    };
  }

  function callApi<ApiMethodName extends keyof BridgeApi>(
    name: ApiMethodName,
    ...args: ApiArgs<ApiMethodName>
  ) {
    const messageId = generateUniqueId();

    const promise = new Promise<any>((resolve, reject) => {
      requestStates.set(messageId, { resolve, reject });
    });

    const messageData: OutMessageData = {
      channel,
      messageId,
      type: 'callMethod',
      name,
      args,
    };

    if ('parent' in target) {
      target.postMessage(messageData, '*');
    } else {
      target.postMessage(JSON.stringify(messageData));
    }

    return promise as ApiMethodResponse<ApiMethodName>;
  }

  function generateUniqueId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  function sanitizeUrl(url?: string | URL) {
    if (!url) return undefined;

    // eslint-disable-next-line no-control-regex
    url = String(url).trim().replace(/[\x00-\x1F\x7F]/g, '');

    if (url.startsWith('//')) {
      return `https:${url}`;
    }

    const UNSAFE_PATTERNS = [
      /^\s*javascript\s*:/i,
      /^\s*data\s*:/i,
      /^\s*vbscript\s*:/i,
      /^\s*file\s*:/i,
      /^\s*about\s*:/i,
      /^\s*blob\s*:/i,
      /^\s*filesystem\s*:/i,
      /^\s*chrome(-extension)?\s*:/i,
      /^\s*moz-extension\s*:/i,
      /^\s*ms-browser-extension\s*:/i,
    ];

    if (UNSAFE_PATTERNS.some((p) => p.test(url))) {
      return undefined;
    }

    if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) {
      return undefined;
    }

    return url;
  }

  function initEvmConnect() {
    const BNB_MAINNET_CAIP = 'eip155:56';

    const EVM_CHAIN_IDS: ChainIdByChain = {
      [BNB_MAINNET_CAIP]: { chain: 'bnb', network: 'mainnet' },
      'eip155:97': { chain: 'bnb', network: 'testnet' },
    };

    const EVM_EIP155_NAMESPACES = {
      eip155: {
        methods: [
          'eth_sendTransaction',
          'eth_signTransaction',
          'personal_sign',
          'eth_sign',
          'eth_signTypedData',
          'eth_signTypedData_v4',
        ],
        chains: Object.keys(EVM_CHAIN_IDS),
        events: ['accountsChanged', 'chainChanged'],
      },
    };

    function caip2ToHexChainId(caip2: string): string {
      const match = /^eip155:(\d+)$/.exec(caip2);

      if (!match) {
        throw new Error('Invalid CAIP-2 chain id');
      }

      return `0x${BigInt(match[1]).toString(16)}`;
    }

    function hexToEip155Caip2(hex: string): string {
      const value = String(hex ?? '');

      if (!value) throw new TypeError('Invalid chain ID');

      return `eip155:${BigInt(value)}`;
    }

    function normalizeHexChainId(hex: string): string {
      const value = String(hex ?? '');

      if (!value) throw new TypeError('Invalid chain ID');

      return `0x${BigInt(value).toString(16)}`;
    }

    function getCaip2ForSessionChain(chain: string, network: string): string | undefined {
      return Object.entries(EVM_CHAIN_IDS).find(
        ([, v]) => v.chain === chain && v.network === network,
      )?.[0];
    }

    type Eip1193Event = 'accountsChanged' | 'chainChanged' | 'connect' | 'disconnect';

    // Read-only EVM JSON-RPC methods we forward through walletConnect_proxyEvmRpc.
    // Mirrors READONLY_EVM_RPC_METHODS in the SDK adapter — the wallet side enforces
    // the same set; this list saves us a round-trip for clearly unsupported methods.
    const READONLY_RPC_METHODS = new Set<string>([
      'eth_blockNumber',
      'eth_getBalance',
      'eth_call',
      'eth_estimateGas',
      'eth_gasPrice',
      'eth_maxPriorityFeePerGas',
      'eth_feeHistory',
      'eth_getTransactionCount',
      'eth_getTransactionByHash',
      'eth_getTransactionReceipt',
      'eth_getCode',
      'eth_getStorageAt',
      'eth_getBlockByNumber',
      'eth_getBlockByHash',
      'eth_getLogs',
      'eth_getBlockTransactionCountByNumber',
      'eth_getBlockTransactionCountByHash',
      'eth_protocolVersion',
      'eth_syncing',
      'net_listening',
      'web3_clientVersion',
    ]);

    // Per-method TTLs (ms) for readonly RPC coalescing. Methods absent from this
    // map are NOT cached (params-keyed methods, balance/nonce/receipt-style data
    // that must stay fresh after sendTx, etc.). Sized to halve evmapi RPS for the
    // most-polled methods without delaying dApp updates (Ethereum block time ~12s, so
    // 1.5s = ~12% of a block).
    const READ_CACHE_TTL_MS: Record<string, number> = {
      eth_blockNumber: 1500,
      eth_gasPrice: 1500,
      eth_maxPriorityFeePerGas: 1500,
      eth_syncing: 30000,
      eth_protocolVersion: 600000,
      net_listening: 600000,
      web3_clientVersion: 600000,
    };

    class EvmInAppConnect {
      private lastGeneratedId = 0;

      private readonly eventListeners = new Map<Eip1193Event, Set<(...args: unknown[]) => void>>();

      private sessionChains: DappSessionChain[] = [];

      private selectedCaip2: string | undefined;

      private readCache = new Map<string, { promise: Promise<unknown>; expiresAt: number }>();

      // Short-TTL cache for silent reconnect to absorb Reown/wagmi polling
      // (eth_accounts is polled at 100+/s; in-flight dedup at the SDK alone leaks
      // about 60% of the load to the worker between successive requests).
      private silentReconnect: {
        promise: Promise<DappConnectionResult<'walletConnect'>>;
        expiresAt: number;
      } | undefined;

      private addListener(event: Eip1193Event, handler: (...args: unknown[]) => void) {
        let set = this.eventListeners.get(event);

        if (!set) {
          set = new Set();
          this.eventListeners.set(event, set);
        }

        set.add(handler);
      }

      private removeListener(event: Eip1193Event, handler: (...args: unknown[]) => void) {
        this.eventListeners.get(event)?.delete(handler);
      }

      private emit(event: Eip1193Event, args: unknown[]) {
        this.eventListeners.get(event)?.forEach((listener) => {
          try {
            listener(...args);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('EvmInAppConnect:emit', err);
          }
        });
      }

      private async requestWc(name: string, args: any[] = []) {
        switch (name) {
          case 'reconnect':
            return callApi('evmConnect:reconnect', args[0]);
          case 'connect':
            return callApi('evmConnect:connect', args[0]);
          case 'sendTransaction':
            return callApi('evmConnect:sendTransaction', args[0]);
          case 'signData':
            return callApi('evmConnect:signData', args[0]);
          default:
            throw new Error(`Unknown wallet connect op: ${name}`);
        }
      }

      private currentChain(): ApiChain | undefined {
        return this.selectedCaip2 ? EVM_CHAIN_IDS[this.selectedCaip2]?.chain : undefined;
      }

      private async proxyReadRpc(method: string, params: unknown[]): Promise<unknown> {
        const ttl = READ_CACHE_TTL_MS[method];
        if (!ttl) {
          return this.dispatchProxyRead(method, params);
        }
        const now = Date.now();
        const cached = this.readCache.get(method);
        if (cached && cached.expiresAt > now) {
          return cached.promise;
        }
        const promise = this.dispatchProxyRead(method, params);
        const entry = { promise, expiresAt: now + ttl };
        this.readCache.set(method, entry);
        promise.catch(() => {
          if (this.readCache.get(method) === entry) this.readCache.delete(method);
        });
        return promise;
      }

      private async dispatchProxyRead(method: string, params: unknown[]): Promise<unknown> {
        const chain = this.currentChain();
        if (!chain) {
          return Promise.reject({ code: 4901, message: 'No selected chain for RPC proxy' });
        }
        const response = await callApi('evmConnect:proxyEvmRpc', { chain, method, params });
        if (response && response.success) {
          return response.result;
        }
        const err = response && 'error' in response ? response.error : undefined;
        return Promise.reject({
          code: err?.code ?? -32603,
          message: err?.message || 'RPC proxy error',
        });
      }

      private get evmChains(): DappSessionChain[] {
        return this.sessionChains.filter((c) =>
          new Set(Object.values(EVM_CHAIN_IDS).map((c) => c.chain)).has(c.chain),
        );
      }

      private getAccountsLower(): string[] {
        return [...new Set(this.evmChains.map((c) => c.address.toLowerCase()))];
      }

      private chainIdHex(): string {
        const caip2 = this.selectedCaip2 ?? getCaip2ForSessionChain(
          this.evmChains[0]?.chain ?? 'bnb',
          this.evmChains[0]?.network ?? 'mainnet',
        );

        if (!caip2) {
          return caip2ToHexChainId(BNB_MAINNET_CAIP);
        }

        return caip2ToHexChainId(caip2);
      }

      private resolveChainForAddress(address: string, caip?: string): { chain: ApiChain; network: ApiNetwork } {
        // Chain-agnostic methods (personal_sign, eth_sign and the like) carry no CAIP, so they land
        // on the only EVM chain we support.
        caip = caip || BNB_MAINNET_CAIP;

        // Cannot use getAddress here, so use toLowerCase instead
        const normalized = address.toLowerCase();

        const targetChain = caip ? EVM_CHAIN_IDS[caip] : undefined;

        if (!targetChain) {
          return { chain: 'bnb', network: 'mainnet' };
        }

        const row = this.evmChains.find(
          (c) => c.address.toLowerCase() === normalized && c.chain === targetChain.chain);

        if (row) {
          return { chain: row.chain, network: row.network };
        }

        return {
          chain: this.evmChains[0]?.chain ?? 'bnb',
          network: this.evmChains[0]?.network ?? 'mainnet',
        };
      }

      private applySessionResult(response: DappConnectionResult<'walletConnect'>) {
        if (!response.success) {
          return;
        }

        const prevAccounts = this.getAccountsLower();
        const prevChainHex = this.chainIdHex();

        this.sessionChains = response.session.chains;
        const evm = this.evmChains;

        if (evm.length) {
          const stillConnected = this.selectedCaip2 !== undefined && evm.some(
            (c) => getCaip2ForSessionChain(c.chain, c.network) === this.selectedCaip2,
          );

          if (!stillConnected) {
            this.selectedCaip2 = getCaip2ForSessionChain(evm[0].chain, evm[0].network);
          }

          const nextAccounts = this.getAccountsLower();
          const nextChainHex = this.chainIdHex();
          // Block-bound and gas-price cache must not leak across chains.
          if (nextChainHex !== prevChainHex) {
            this.readCache.clear();
          }
          // Only emit when state actually changed. Without this, every silent reconnect
          // (Reown polls eth_accounts ~100/s) re-emits accountsChanged and pins
          // React-based dapps in a re-render loop until the JS heap OOMs.
          // Fire `connect` on the disconnected->connected transition (prev had no accounts)
          // even if the resolved chain matches our default fallback ('0x1' for mainnet).
          if (prevAccounts.length === 0 || prevChainHex !== nextChainHex) {
            this.emit('connect', [{ chainId: nextChainHex }]);
          }
          if (prevAccounts.length !== nextAccounts.length
            || prevAccounts.some((a, i) => a !== nextAccounts[i])) {
            this.emit('accountsChanged', [nextAccounts]);
          }
        }
      }

      private async connectWallet(silent: boolean): Promise<DappConnectionResult<'walletConnect'>> {
        if (silent) {
          const now = Date.now();
          const cached = this.silentReconnect;
          if (cached && cached.expiresAt > now) {
            return cached.promise;
          }
          const id = ++this.lastGeneratedId;
          const promise = this.requestWc('reconnect', [id]) as Promise<DappConnectionResult<'walletConnect'>>;
          const entry = { promise, expiresAt: now + 500 };
          this.silentReconnect = entry;
          promise.then(
            (resp) => {
              if (!resp || !resp.success) {
                if (this.silentReconnect === entry) this.silentReconnect = undefined;
              }
            },
            () => {
              if (this.silentReconnect === entry) this.silentReconnect = undefined;
            },
          );
          return promise;
        }
        const id = ++this.lastGeneratedId;

        const metadata = {
          url: window.origin,
          name: document.querySelector<HTMLMetaElement>('meta[property*="og:title"]')?.content
            || document.title,
          description: '',
          icons: [document.querySelector<HTMLLinkElement>('link[rel*="icon"]')?.href
            || `${window.location.origin}/favicon.ico` || ''],
        };

        const payload = {
          id,
          params: {
            id,
            expiryTimestamp: 0,
            relays: [],
            proposer: {
              publicKey: '',
              metadata,
            },
            requiredNamespaces: {},
            optionalNamespaces: {
              ...EVM_EIP155_NAMESPACES,
            },
            pairingTopic: '',
          },
        };

        const unifiedPayload = {
          protocolType: 'walletConnect',
          transport: 'extension',
          protocolData: payload,
          permissions: {
            isPasswordRequired: false,
            isAddressRequired: false,
          },
          requestedChains: Object.values(EVM_CHAIN_IDS),
        };

        return this.requestWc('connect', [unifiedPayload]) as Promise<DappConnectionResult<'walletConnect'>>;
      }

      private async request(args: { method: string; params?: readonly unknown[] | Record<string, unknown> }) {
        const { method } = args;
        const params = (args.params ?? []) as unknown[];

        try {
          switch (method) {
            case 'eth_requestAccounts': {
              const result = await this.connectWallet(false);

              if (!result?.success || !('session' in result)) {
                return [];
              }

              this.applySessionResult(result);

              return this.getAccountsLower();
            }
            case 'eth_accounts': {
              const result = await this.connectWallet(true);

              if (!result?.success || !('session' in result)) {
                return [];
              }

              this.applySessionResult(result);

              if (!result?.success) {
                return [];
              }

              return this.getAccountsLower();
            }
            case 'eth_chainId':
              return this.chainIdHex();
            case 'net_version':
              return String(BigInt(this.chainIdHex()));
            case 'wallet_switchEthereumChain': {
              const p = params[0] as { chainId?: string } | undefined;

              const chainIdHex = p?.chainId ? normalizeHexChainId(p.chainId) : '';
              const targetCaip = hexToEip155Caip2(chainIdHex);

              if (!EVM_CHAIN_IDS[targetCaip]) {
                return Promise.reject({
                  code: 4902,
                  message: 'Unrecognized chain',
                });
              }

              const match = this.evmChains.find(
                (c) => getCaip2ForSessionChain(c.chain, c.network) === targetCaip,
              );

              if (!match) {
                return Promise.reject({
                  code: 4902,
                  message: 'Chain not added',
                });
              }

              this.selectedCaip2 = targetCaip;
              // Block-bound and gas-price cache must not leak across chains.
              this.readCache.clear();
              this.emit('chainChanged', [this.chainIdHex()]);

              return undefined;
            }
            case 'eth_sendTransaction':
            case 'eth_signTransaction': {
              let txParams = params[0] as EvmTransactionParams | undefined;

              if (txParams && !txParams?.chainId) {
                txParams = {
                  ...txParams,
                  chainId: this.selectedCaip2 ? caip2ToHexChainId(this.selectedCaip2) : undefined,
                };
              }

              if (!txParams?.from) {
                return Promise.reject({ code: -32602, message: 'Invalid params: missing from' });
              }

              if (!txParams.chainId) {
                return Promise.reject({ code: -32602, message: 'Invalid params: missing chainId' });
              }

              const targetCaip = hexToEip155Caip2(txParams.chainId);
              const isValidCaip = Object.keys(EVM_CHAIN_IDS).some((caip) => caip === targetCaip);

              const { chain } = this.resolveChainForAddress(txParams.from, targetCaip);

              if (!isValidCaip) {
                return Promise.reject({ code: 4100, message: 'Unknown chain for signer' });
              }

              const wcId = ++this.lastGeneratedId;

              const unifiedPayload = {
                id: String(wcId),
                chain,
                payload: {
                  isSignOnly: method === 'eth_signTransaction',
                  url: window.origin,
                  address: txParams.from,
                  data: txParams,
                },
              };
              const response = await this.requestWc(
                'sendTransaction',
                [unifiedPayload],
              );

              if (!response?.success || !('result' in response)) {
                return Promise.reject({
                  code: 4001,
                  message: 'Rejected',
                });
              }

              return response.result.result;
            }
            case 'personal_sign': {
              const msg = params[0] as string;
              const addr = params[1] as string;

              return this.signPersonalOrEth(addr, msg);
            }
            case 'eth_sign': {
              const addr = params[0] as string;
              const data = params[1] as string;

              return this.signPersonalOrEth(addr, data);
            }
            case 'eth_signTypedData':
            case 'eth_signTypedData_v4': {
              const addr = params[0] as string;
              const raw = params[1] as string | Record<string, unknown>;

              return this.signTypedData(addr, raw);
            }
            default:
              if (READONLY_RPC_METHODS.has(method)) {
                return this.proxyReadRpc(method, params);
              }
              return Promise.reject({ code: -32601, message: `Unsupported method: ${method}` });
          }
        } catch (err: unknown) {
          if (err && typeof err === 'object' && 'code' in err) {
            return Promise.reject(err);
          }
          // eslint-disable-next-line no-console
          console.error('EvmInAppConnect:request', err);
          return Promise.reject({ code: -32603, message: err instanceof Error ? err.message : 'Internal error' });
        }
      }

      private async signPersonalOrEth(address: string, data: string) {
        const { chain } = this.resolveChainForAddress(address);

        const id = ++this.lastGeneratedId;

        const unifiedPayload = {
          id: String(id),
          chain,
          payload: {
            url: window.origin,
            address,
            data,
          },
        };

        const response = await this.requestWc(
          'signData',
          [unifiedPayload],
        );

        if (!response?.success || !('result' in response)) {
          return Promise.reject({
            code: 4001,
            message: 'Rejected',
          });
        }

        return response.result.result;
      }

      private async signTypedData(address: string, raw: string | Record<string, unknown>) {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) as Record<string, unknown> : raw;

        const domain = parsed.domain as Record<string, unknown>;
        const types = parsed.types as Record<string, Array<{ name: string; type: string }>>;
        const primaryType = parsed.primaryType as string;

        const message = parsed.message as Record<string, unknown>;

        if (!domain || !types || !primaryType || !message) {
          return Promise.reject({ code: -32602, message: 'Invalid typed data' });
        }

        const { chain } = this.resolveChainForAddress(address);

        const id = ++this.lastGeneratedId;

        const unifiedPayload = {
          id: String(id),
          chain,
          payload: {
            url: window.origin,
            address,
            eip712: {
              domain,
              types,
              primaryType,
              message,
            },
          },
        };

        const response = await this.requestWc(
          'signData',
          [unifiedPayload],
        );

        if (!response?.success || !('result' in response)) {
          return Promise.reject({
            code: 4001,
            message: 'Rejected',
          });
        }

        return response.result.result;
      }

      readonly provider = {
        isIONWallet: true,
        request: (reqArgs: any) => this.request(reqArgs),
        on: (event: string, handler: (...args: unknown[]) => void) => {
          this.addListener(event as Eip1193Event, handler);
          return () => {
            this.removeListener(event as Eip1193Event, handler);
          };
        },
        removeListener: (event: string, handler: (...args: unknown[]) => void) => {
          this.removeListener(event as Eip1193Event, handler);
        },
      };
    }

    const evm = new EvmInAppConnect();

    registerEvmInjectedWallet({
      info: {
        uuid: (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `evm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
        name: appName,
        icon: `data:image/svg+xml,${encodeURIComponent(icon)}`,
        rdns: 'io.ionwallet',
      },
      provider: evm.provider,
    });
  }
}

export const initConnectorString = initConnector.toString();
